import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';

import styles from './FishGroupDetailModal.module.scss';

export type StockingActionKind = 'assign' | 'unassign';

interface StockingMembershipProps {
    /** The stocking's display name, or null for a group in none. */
    cycleName: string | null;
    writesDisabled: boolean;
    onAction?: (kind: StockingActionKind) => void;
}

/** Which stocking the group is in, with Add to stocking or Remove from stocking. */
export default function StockingMembership(props: StockingMembershipProps) {
    const { cycleName, writesDisabled, onAction } = props;
    const { t } = useTranslation('fishGroups');
    const labelId = useId();
    const noteId = useId();
    const kind: StockingActionKind = cycleName === null ? 'assign' : 'unassign';
    const reason = writesDisabled ? t('board.actions.notInDemoMode') : undefined;

    return (
        <Card as="section" variant="subtle" padding="sm" radius="sm" aria-labelledby={labelId}>
            <div className={styles.stocking}>
                <div className={styles.fact}>
                    <Caption as="h3" id={labelId}>
                        {t('siteActions.detail.stocking.label')}
                    </Caption>
                    <p className={styles.factValue}>{cycleName ?? t('siteActions.noStocking')}</p>
                </div>
                {onAction && (
                    <Button
                        variant="secondary"
                        disabled={writesDisabled}
                        tooltip={reason}
                        aria-describedby={writesDisabled ? noteId : undefined}
                        onClick={() => onAction(kind)}>
                        {kind === 'assign'
                            ? t('siteActions.detail.stocking.add')
                            : t('siteActions.detail.stocking.remove')}
                    </Button>
                )}
            </div>
            {/* A disabled button takes no focus, so the reason is a visible note too. */}
            {onAction && writesDisabled && (
                <p id={noteId} className={styles.note}>
                    {reason}
                </p>
            )}
        </Card>
    );
}
